import { Badge, Box, Text } from '@chakra-ui/react';
import { DetailProduct } from '@diamond/shared/types';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';

/**
 * Minimal product shape required for displaying promo pricing data.
 */
type Promoable = Pick<
  DetailProduct,
  | 'minimum_order_quantity'
  | 'discount_promos'
  | 'bundled_discount_promos'
  | 'bundled_free_good_promos'
  | 'prices'
>;

/**
 * Props accepted by {@link PromoPriceIntegration}.
 */
type PromoPriceProps<T extends Promoable> = {
  data: T;
  promoDeductionAmount?: number;
  promoIsDiscount?: boolean;
  isBundled?: boolean;
  hasActiveDiscount?: boolean;
  badgeLabelOverride?: string;
};

type DiscountPromoSource =
  | Promoable['discount_promos'][number]
  | Promoable['bundled_discount_promos'][number];

/**
 * Renders strike-through price and discount badge for active promos.
 * Handles bundled vs regular promos and optional overrides coming from the caller.
 */
export function PromoPriceIntegration<T extends Promoable>({
  data,
  promoDeductionAmount,
  promoIsDiscount,
  isBundled,
  hasActiveDiscount,
  badgeLabelOverride,
}: PromoPriceProps<T>): JSX.Element | null {
  const primaryDiscountPromo = data.discount_promos?.[0];
  const bundledDiscountPromo = data.bundled_discount_promos?.[0];

  const selectedDiscountPromo: DiscountPromoSource | undefined = isBundled
    ? bundledDiscountPromo
    : primaryDiscountPromo;

  let resolvedDeductionAmount: number | null = null;
  if (typeof promoDeductionAmount === 'number') {
    resolvedDeductionAmount = promoDeductionAmount;
  } else if (typeof selectedDiscountPromo?.deduction_amount === 'number') {
    resolvedDeductionAmount = selectedDiscountPromo.deduction_amount;
  }

  let isDiscount: boolean | undefined = promoIsDiscount;
  if (isDiscount === undefined) {
    isDiscount = selectedDiscountPromo?.is_discount;
  }

  if (!isDiscount || resolvedDeductionAmount === null) {
    return null;
  }

  const discountPercentage = resolvedDeductionAmount / 100;

  let isQualifiedByQuantity = false;
  if (!hasActiveDiscount && !isBundled && primaryDiscountPromo) {
    const discountMinimumQuantity = primaryDiscountPromo.minimum_order_quantity;
    if (typeof discountMinimumQuantity === 'number') {
      isQualifiedByQuantity =
        data.minimum_order_quantity >= discountMinimumQuantity;
    }
  }

  const isActive = Boolean(hasActiveDiscount) || isQualifiedByQuantity;
  const originalPrice = data.prices?.[0]?.price;
  const badgeColor = isActive ? '#FF0000' : '#006BD1';
  const badgeBackground = isActive ? '#FFDADA' : '#F3F4F5';
  const badgePrefix = isActive ? '' : '*';
  const discountLabel =
    badgeLabelOverride ?? `${badgePrefix}${discountPercentage}%`;

  /**
   * Badge + price section mirrors catalog/product cards:
   * - strike-through appears once discount is active
   * - badge switches color/prefix depending on activation state
   */
  return (
    <Box display="flex" gap="1" alignItems="center">
      {isActive && originalPrice !== undefined && (
        <Text
          color="gray.75"
          textDecoration="line-through"
          fontSize={{ base: 'xs', md: '12px' }}
          lineHeight="1.25"
        >
          {buyerCurrencyFormatter(originalPrice)}
        </Text>
      )}
      <Badge
        color={badgeColor}
        bgColor={badgeBackground}
        rounded="4px"
        fontSize={{ base: '2xs', md: '12px' }}
      >
        {discountLabel}
      </Badge>
    </Box>
  );
}
